import { useCallback, useEffect, useMemo, useState } from "react";
import RecentTracker from "@/components/RecentTracker";
import { useUrlState } from "@/lib/useUrlState";
import { RecordSwitcher } from "@/components/RecordSwitcher";
import { useNavigate } from "react-router-dom";
import {
  FileText, Table2, Info, Loader2, PlugZap, FileStack, Mail, MessageSquare,
  FolderOpen, Activity,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import ProjectInfoTab from "./components/ProjectInfoTab";
import EstimateTab from "./components/EstimateTab";
import ProposalTab from "./components/ProposalTab";
import PipelineSyncCard from "./components/PipelineSyncCard";
import ChangeOrdersTab from "./components/ChangeOrdersTab";
import { EmailsTab, NotesPanel } from "./components/BidCardTabs";
import BidFilesTab from "./components/BidFilesTab";
import { BoardStatusChip, BOARD_STATUS_CHIP } from "./components/BidStatusChips";
import { estApi, preconApi } from "./lib/api";

const ESTIMATE_TABS = [
  { key: "info", label: "Info", icon: Info },
  { key: "estimate", label: "Estimate", icon: Table2 },
  { key: "proposal", label: "Proposal", icon: FileText },
];
const NOTES_TAB = { key: "notes", label: "Notes", icon: MessageSquare };
const BID_TABS = [
  { key: "emails", label: "Emails", icon: Mail },
];
const INTEGRATIONS_TAB = { key: "procore", label: "Integrations", icon: PlugZap };
const TAIL_TABS = [
  { key: "files", label: "Files", icon: FolderOpen },
  { key: "activity", label: "Activity", icon: Activity },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" }) : "");

/** BID MASTER §1 — the Estimate workspace: one bid, full page, bookmarkable at /bids/:id. */
export default function BidWorkspacePage({ jobId, estimateId }) {
  const navigate = useNavigate();
  const { access } = useAuth();
  const canEdit = (access?.estimating || "none") === "write";
  const [job, setJob] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [missing, setMissing] = useState(false);
  const [tab, setTab] = useUrlState("tab", "estimate");
  const [switcher, setSwitcher] = useState([]);
  const [statuses, setStatuses] = useState([]);


  const loadJob = useCallback(async () => {
    if (!jobId) return null;
    try { const j = await preconApi.getJob(jobId); setJob(j); return j; } catch { setMissing(true); return null; }
  }, [jobId]);
  const loadEstimate = useCallback(async (id) => {
    if (!id) { setEstimate(null); return; }
    try { setEstimate(await estApi.getEstimate(id)); } catch { setEstimate(null); }
  }, []);
  const [ready, setReady] = useState(false);
  // Full-screen Estimate / Proposal: hides the app chrome and the bid header; Esc exits.
  const [expanded, setExpanded] = useState(false);
  const toggleExpand = () => setExpanded((v) => !v);
  useEffect(() => {
    if (!expanded) return undefined;
    const esc = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [expanded]);
  useEffect(() => { if (!["estimate", "proposal"].includes(tab)) setExpanded(false); }, [tab]);
  const reload = useCallback(async () => { const j = await loadJob(); await loadEstimate(j?.estimate_id || estimateId); setReady(true); }, [loadJob, loadEstimate, estimateId]);

  useEffect(() => { setJob(null); setEstimate(null); setMissing(false); setReady(false); reload(); }, [reload]);
  useEffect(() => {
    preconApi.switcher().then((r) => setSwitcher(r.items || [])).catch(() => {});
    estApi.getEstimateFilters().then((r) => setStatuses((r.statuses || []).map((s) => s.name))).catch(() => {});
  }, []);

  const tabs = useMemo(() => {
    const est = estimate ? (estimate.status === "Won" ? [...ESTIMATE_TABS, { key: "changeorders", label: "Change Orders", icon: FileStack }] : ESTIMATE_TABS) : [];
    // Integrations sits right of Notes; it only exists once there is an estimate.
    // Notes sits right of Proposal (or first when there is no estimate yet).
    const withNotes = job ? [...est.slice(0, 3), NOTES_TAB, ...est.slice(3)] : est;
    return [...withNotes, ...(job ? BID_TABS : []), ...(estimate ? [INTEGRATIONS_TAB] : []), ...(job ? TAIL_TABS : [])];
  }, [estimate, job]);
  // Only once both the bid and its estimate are in, or the Estimate landing tab would be skipped.
  useEffect(() => { if (ready && tabs.length && !tabs.some((t) => t.key === tab)) setTab(tabs[0].key, { replace: true }); }, [tabs, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="workspace-missing">That bid no longer exists.<button type="button" className="underline" onClick={() => navigate("/bid-board")}>Back to the board</button></div>;
  if (!job && !estimate) return <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening the workspace…</div>;

  const name = job?.project_name || estimate?.project_name || "";
  const location = job ? [job.street, job.city, job.state].filter(Boolean).join(", ") : [estimate?.city, estimate?.state].filter(Boolean).join(", ");

  return (
    <div className={expanded ? "fixed inset-0 z-[60] flex flex-col overflow-hidden bg-background" : "flex flex-1 flex-col overflow-hidden"} data-testid="bid-workspace" data-expanded={expanded ? "1" : "0"}>
      <RecentTracker kind="bid" refId={jobId || estimateId} label={job?.project_name || estimate?.project_name || estimate?.name}
                     sub={`Bid${(job?.invitations || [])[0]?.gc_name ? ` · ${job.invitations[0].gc_name}` : estimate?.customer_name ? ` · ${estimate.customer_name}` : ""}`} path={window.location.pathname} />
      {!expanded && <div className="sticky top-0 z-20 bg-background px-4 pt-3 max-sm:px-3" data-testid="workspace-header">
        <div className="rounded-lg border border-slate-200 bg-card px-4 py-2.5 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="min-w-0">
              {job ? (
                <RecordSwitcher variant="title" currentId={job.id} currentLabel={name} empty="No bids match." testid="workspace-switcher"
                                items={switcher.map((s) => ({ id: s.id, label: s.project_name, meta: s.board_status }))}
                                onPick={(v) => navigate(`/bids/${v}?tab=${tab}`)} />
              ) : (
                <h1 className="truncate text-lg font-semibold leading-tight tracking-tight text-slate-900 dark:text-slate-100" data-testid="workspace-title">{name}</h1>
              )}
              <div className="mt-0.5 text-[11px] leading-tight text-slate-500" data-testid="workspace-location">{location || "No address yet"}</div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-3">
              <div data-testid="workspace-status">{job ? <BoardStatusChip job={job} canEdit={canEdit} onChanged={reload} /> : <span className={`rounded border px-1.5 py-0.5 text-[10px] ${BOARD_STATUS_CHIP[estimate?.status] || "border-border"}`}>{estimate?.status}</span>}</div>
              {estimate && <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100" data-testid="workspace-total-sales">{Number(estimate.total_sales || 0).toLocaleString("en-US", { style: "currency", currency: "USD" })}</span>}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1" data-testid="workspace-tabs">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`workspace-tab-${t.key}`}
                    className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors ${tab === t.key ? "border-[#007BFF] font-semibold text-[#007BFF]" : "border-transparent font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
              {t.label}
            </button>))}
        </div>
      </div>}

      <div className="page-body">
        {job && !estimate && (
          <div className="mb-4 rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground" data-testid="workspace-no-estimate">
            No estimate yet. Set the status to <b>New Bid</b> or <b>Budget</b> and the estimate (takeoff upload, proposal) is created here.
          </div>)}
        {estimate && (
          <div className={tab === "info" ? "max-w-7xl" : ""}>
            {tab === "info" && <ProjectInfoTab estimate={estimate} statuses={statuses} job={job} onJobChanged={reload} onSaved={(s) => setEstimate((p) => ({ ...p, ...s }))} />}
            {tab === "estimate" && <EstimateTab estimate={estimate} expanded={expanded} onToggleExpand={toggleExpand} onTotals={(r) => setEstimate((e) => ({ ...e, total_sales: r.total_sales, total_gm_dollars: r.total_gm_dollars, total_gm_percent: r.total_gm_percent, line_count: r.line_count }))} />}
            {tab === "proposal" && <ProposalTab estimate={estimate} expanded={expanded} onToggleExpand={toggleExpand} onStatusChange={() => reload()} />}
            {tab === "procore" && <div className="max-w-4xl space-y-4"><PipelineSyncCard estimate={estimate} onRefresh={reload} /></div>}
            {tab === "changeorders" && <ChangeOrdersTab estimate={estimate} onRefresh={reload} />}
          </div>)}
        {job && tab === "emails" && <div className="max-w-5xl rounded-lg border border-border bg-card p-3"><EmailsTab jobId={job.id} /></div>}
        {job && tab === "notes" && <div className="max-w-3xl"><NotesPanel job={job} onChanged={reload} /></div>}
        {job && tab === "files" && <BidFilesTab job={job} />}
        {job && tab === "activity" && <ActivityList job={job} />}
      </div>
    </div>
  );
}

function ActivityList({ job }) {
  const events = [...(job.events || [])].sort((a, b) => (a.at < b.at ? 1 : -1));
  if (!events.length) return <div className="text-xs text-muted-foreground">Nothing logged yet.</div>;
  return (
    <ul className="max-w-4xl divide-y divide-border rounded-lg border border-border bg-card" data-testid="workspace-activity">
      {events.map((e) => (
        <li key={e.id} className="flex items-start gap-3 px-3 py-1.5 text-xs" data-testid={`workspace-event-${e.id}`}>
          <span className="w-28 shrink-0 text-muted-foreground">{fmt(e.at)}</span>
          <span className="w-44 shrink-0 font-mono text-[11px]">{e.kind}</span>
          <span className="w-40 shrink-0 truncate text-muted-foreground">{e.actor}</span>
          <span className="flex-1 truncate text-muted-foreground" title={JSON.stringify(e.detail)}>{summarize(e.detail)}</span>
          <span className="shrink-0 rounded bg-muted px-1 text-[10px]">{e.source}</span>
        </li>))}
    </ul>
  );
}

const summarize = (d) => {
  if (!d || typeof d !== "object") return "";
  if (d.from !== undefined && d.to !== undefined) return `${d.from ?? "—"} → ${d.to}${d.trigger ? ` (${d.trigger})` : ""}`;
  if (d.before && d.after) return `${typeof d.before === "object" ? d.before.bid_status : d.before} → ${d.after}`;
  return Object.entries(d).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
};
